import React, { useCallback, useMemo, useState } from 'react';

import { Link } from 'react-router-dom';

import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import {
  Box,
  Chip,
  IconButton,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { UmbrellaChildSummary } from '~/services/ExperiencesService';

import UmbrellaChildImagePicker from './UmbrellaChildImagePicker';
import { resolveImageUrl } from './umbrellaUtils';

interface Props {
  items: UmbrellaChildSummary[];
  brand: string;
  onChildUpdated?: () => void;
}

export default function UmbrellaChildrenBanner({ items, brand, onChildUpdated }: Props) {
  const [pickerChild, setPickerChild] = useState<UmbrellaChildSummary | null>(null);

  const handleOpenPicker = useCallback((child: UmbrellaChildSummary) => {
    setPickerChild(child);
  }, []);

  const handleClosePicker = useCallback(() => {
    setPickerChild(null);
  }, []);

  const handleImageUpdated = useCallback(() => {
    onChildUpdated?.();
  }, [onChildUpdated]);

  const siblingIds = useMemo(() => items.map((c) => c.id), [items]);

  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        Grouped Offers ({items.length})
      </Typography>
      <Paper variant="outlined" sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TableContainer sx={{ flex: 1, maxHeight: 240, overflowY: 'auto', overflowX: 'hidden' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ py: 0.5, width: 50, px: 1 }}>
                  <Typography variant="caption" fontWeight={600}>
                    Image
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5, width: 90 }}>
                  <Typography variant="caption" fontWeight={600}>
                    ID
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5 }}>
                  <Typography variant="caption" fontWeight={600}>
                    Name
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5, width: 90 }}>
                  <Typography variant="caption" fontWeight={600}>
                    Status
                  </Typography>
                </TableCell>
                <TableCell sx={{ py: 0.5, width: 40, px: 0 }}>
                  <Typography variant="caption" fontWeight={600}>
                    Hero
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((child) => (
                <TableRow key={child.id} hover>
                  <TableCell sx={{ py: 0.5, px: 1 }}>
                    {child.heroImageUrl ? (
                      <Box
                        component="img"
                        src={resolveImageUrl(child.heroImageUrl)}
                        alt={child.title}
                        sx={{ width: 44, height: 32, objectFit: 'cover', borderRadius: 0.5 }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 44,
                          height: 32,
                          bgcolor: 'grey.100',
                          borderRadius: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography sx={{ fontSize: '0.5rem', color: 'text.disabled' }}>No img</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Tooltip title={`Open ${child.providerId} in admin`}>
                      <MuiLink component={Link} to={`/experience/${child.id}`} variant="body2" fontWeight={500}>
                        {child.providerId}
                      </MuiLink>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Tooltip title={child.title} placement="top-start">
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.3,
                        }}
                      >
                        {child.title}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Chip
                      size="small"
                      label={child.curationStatus}
                      color={child.curationStatus === 'APPROVED' ? 'success' : 'default'}
                      sx={{ fontSize: '0.6rem', height: 18 }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.5, px: 0 }}>
                    <Tooltip title="Change hero image">
                      <IconButton size="small" aria-label="Change hero image" onClick={() => handleOpenPicker(child)}>
                        <PhotoCameraIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {pickerChild && (
        <UmbrellaChildImagePicker
          open={!!pickerChild}
          onClose={handleClosePicker}
          childId={pickerChild.id}
          childTitle={pickerChild.title}
          brand={brand}
          siblingIds={siblingIds}
          onImageUpdated={handleImageUpdated}
        />
      )}
    </Box>
  );
}
